import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Star, RefreshCw, Send, Smartphone, Tv, CheckCircle2, Sparkles } from 'lucide-react';
import { LoadingSpinner } from './core';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDT = (iso) => { try { return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return '—'; } };

function Stars({ n }) {
  return (
    <span className="inline-flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => <Star key={i} size={13} className={i <= (n || 0) ? 'text-amber-400 fill-amber-400' : 'text-white/20'} />)}
    </span>
  );
}

export function StoreReviewsTab({ theme }) {
  const light = theme === 'light';
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [reply, setReply] = useState({});
  const [sending, setSending] = useState(null);
  const [suggesting, setSuggesting] = useState(null);

  const suggest = async (rv) => {
    setSuggesting(rv.id);
    try {
      const { data: r } = await axios.post(`${API}/admin/store-reviews/${rv.id}/suggest`, {}, { withCredentials: true });
      setReply(p => ({ ...p, [rv.id]: r.suggestion }));
    } catch (err) { alert(err.response?.data?.detail || 'Erreur IA'); }
    setSuggesting(null);
  };
  const th = light ? 'text-gray-500' : 'text-white/45';
  const tx = light ? 'text-gray-900' : 'text-white';
  const cardCls = light ? 'bg-white border-gray-200' : 'bg-white/[0.03] border-white/10';

  const load = useCallback(() => {
    axios.get(`${API}/admin/store-reviews`, { withCredentials: true }).then(r => setData(r.data)).catch(() => setData({ items: [], config: {} }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const doSync = async () => {
    setSyncing(true);
    try {
      const { data: r } = await axios.post(`${API}/admin/store-reviews/sync`, {}, { withCredentials: true });
      const errs = [r.ios?.error && `iOS : ${r.ios.error}`, r.android?.error && `Android : ${r.android.error}`, r.android_tv?.error && `Google Play TV : ${r.android_tv.error}`].filter(Boolean);
      if (errs.length) alert(errs.join('\n'));
      load();
    } catch { alert('Erreur de synchronisation'); }
    setSyncing(false);
  };

  const sendReply = async (rv) => {
    const msg = (reply[rv.id] || '').trim();
    if (!msg) return;
    setSending(rv.id);
    try {
      await axios.post(`${API}/admin/store-reviews/${rv.id}/reply`, { message: msg }, { withCredentials: true });
      setReply(p => ({ ...p, [rv.id]: '' }));
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur lors de la publication'); }
    setSending(null);
  };

  return (
    <div data-testid="store-reviews-tab">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className={`text-2xl font-bold ${tx}`}>Avis Stores</h1>
          <p className={`text-xs mt-1 ${th}`}>Avis App Store, Google Play mobile & app TV — répondez, c'est publié directement sur le store. (Amazon Appstore : pas d'API d'avis, consultez la fiche produit Amazon.)</p>
        </div>
        <button onClick={doSync} disabled={syncing} data-testid="store-reviews-sync-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold bg-[#E9641A] text-white hover:bg-[#d4570f] disabled:opacity-50 transition-colors">
          <RefreshCw size={13} className={syncing ? 'animate-spin' : ''} />{syncing ? 'Synchronisation…' : 'Actualiser les avis'}
        </button>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold ${data?.config?.ios ? 'bg-green-500/15 text-green-500' : 'bg-white/8 text-white/40'}`}>
          <Smartphone size={11} />App Store iOS : {data?.config?.ios ? 'connecté' : 'non configuré'}
        </span>
        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold ${data?.config?.android ? 'bg-green-500/15 text-green-500' : light ? 'bg-amber-50 text-amber-600' : 'bg-amber-500/10 text-amber-400'}`}>
          <Smartphone size={11} />Google Play : {data?.config?.android ? 'connecté' : 'en attente de la clé JSON'}
        </span>
        <span data-testid="store-reviews-config-androidtv" className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold ${data?.config?.android_tv ? 'bg-green-500/15 text-green-500' : light ? 'bg-amber-50 text-amber-600' : 'bg-amber-500/10 text-amber-400'}`}>
          <Tv size={11} />Google Play TV : {data?.config?.android_tv ? 'connecté' : 'en attente de la clé JSON'}
        </span>
      </div>

      {!data ? <LoadingSpinner /> : data.items.length === 0 ? (
        <div className={`rounded-xl border p-10 text-center ${cardCls}`}>
          <Star size={26} className={`mx-auto mb-2 ${light ? 'text-gray-300' : 'text-white/15'}`} />
          <p className={`text-sm ${th}`}>Aucun avis pour le moment — cliquez sur « Actualiser les avis »</p>
          <p className={`text-[11px] mt-1 ${th}`}>Les avis sont aussi récupérés automatiquement toutes les 6 heures, avec notification par email.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {data.items.map(rv => (
            <div key={rv.id} className={`rounded-xl border p-4 ${cardCls}`} data-testid={`store-review-${rv.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${rv.store === 'ios' ? 'bg-white/10 text-white/70' : rv.store === 'android_tv' ? 'bg-sky-500/15 text-sky-400' : 'bg-green-500/15 text-green-400'}`}>{rv.store === 'ios' ? ' App Store' : rv.store === 'android_tv' ? 'Google Play TV' : 'Google Play'}</span>
                  <Stars n={rv.rating} />
                  <span className={`text-xs font-bold ${tx}`}>{rv.title || '(sans titre)'}</span>
                </div>
                <span className={`text-[10px] ${th}`}>{rv.reviewer || 'Anonyme'}{rv.territory ? ` · ${rv.territory}` : ''} · {fmtDT(rv.created_at)}</span>
              </div>
              {rv.body && <p className={`text-xs mt-2 whitespace-pre-wrap ${light ? 'text-gray-700' : 'text-white/75'}`}>{rv.body}</p>}
              {rv.our_response ? (
                <div className={`mt-3 ml-4 rounded-lg p-3 text-xs ${light ? 'bg-green-50 border border-green-200' : 'bg-green-500/10 border border-green-500/20'}`}>
                  <p className="text-[10px] font-bold text-green-500 mb-1 inline-flex items-center gap-1"><CheckCircle2 size={11} />Votre réponse publiée{rv.replied_at ? ` · ${fmtDT(rv.replied_at)}` : ''}</p>
                  <p className={`whitespace-pre-wrap ${light ? 'text-gray-700' : 'text-white/75'}`}>{rv.our_response}</p>
                </div>
              ) : (
                <div className="flex gap-2 mt-3 items-stretch">
                  <textarea value={reply[rv.id] || ''} onChange={e => setReply(p => ({ ...p, [rv.id]: e.target.value }))} rows={2}
                    maxLength={rv.store?.startsWith('android') ? 350 : 5970}
                    placeholder="Répondre à cet avis… (publié publiquement sur le store)" data-testid={`review-reply-input-${rv.id}`}
                    className={`flex-1 text-xs rounded-lg p-2.5 border outline-none resize-none ${light ? 'bg-white border-gray-200 text-gray-900' : 'bg-white/5 border-white/10 text-white placeholder-white/30'}`} />
                  <div className="flex flex-col gap-1.5">
                    <button disabled={suggesting === rv.id} onClick={() => suggest(rv)} data-testid={`review-suggest-${rv.id}`}
                      className={`px-3 py-1.5 rounded-lg text-[11px] font-bold inline-flex items-center gap-1.5 transition-colors disabled:opacity-50 ${light ? 'bg-purple-100 text-purple-700 hover:bg-purple-200' : 'bg-purple-500/15 text-purple-300 hover:bg-purple-500/25'}`}>
                      <Sparkles size={11} />{suggesting === rv.id ? 'Rédaction…' : 'Suggestion IA'}
                    </button>
                    <button disabled={sending === rv.id || !(reply[rv.id] || '').trim()} onClick={() => sendReply(rv)} data-testid={`review-reply-send-${rv.id}`}
                      className="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-[#E9641A] text-white hover:bg-[#d4570f] disabled:opacity-40 transition-colors inline-flex items-center gap-1.5">
                      <Send size={11} />{sending === rv.id ? 'Publication…' : 'Publier'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
